import { NoSmokingOutlineIcon, SmokingOutlineIcon } from '@ikyucom/design-system-icons-react'
import { RadioButton } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import { FilterChipsWithPopover, useClose } from './Popover'
import { createColor } from './criteria'
import { RadioButtonPopup } from './popup'
import type {
  FilterCriteria,
  SmokingAllowedCheckType,
  SmokingAllowed as SmokingAllowedType,
} from './types'

export type useSetSmokingAllowed = () => (smokingAllowed: SmokingAllowedCheckType) => void

type Props = {
  useFilterCriteria: () => FilterCriteria | undefined
  useSmokingAllowed: () => SmokingAllowedType
  useSetSmokingAllowed: useSetSmokingAllowed
  useEnabledSmokingAllowed: () => SmokingAllowedType
}

export function SmokingAllowed({
  useFilterCriteria,
  useSmokingAllowed,
  useSetSmokingAllowed,
  useEnabledSmokingAllowed,
}: Props) {
  const { allow: hasAllow, notAllow: hasNotAllow } = useSmokingAllowed()
  const setSmokingAllowed = useSetSmokingAllowed()
  const criteria = useFilterCriteria()
  const enabled = useEnabledSmokingAllowed()

  if (!hasAllow && !hasNotAllow) {
    return null
  }

  return (
    <SmokingAllowedWithPopover
      hasNotSmokingAllowed={hasNotAllow}
      hasSmokingAllowed={hasAllow}
      criteria={criteria}
      setSmokingAllowed={setSmokingAllowed}
      enabled={enabled}
    />
  )
}

type DraftSmokingAllowed = {
  hasSmokingAllowed: boolean
  hasNotSmokingAllowed: boolean
}

function SmokingAllowedWithPopover({
  criteria,
  hasSmokingAllowed,
  hasNotSmokingAllowed,
  setSmokingAllowed,
  enabled,
}: {
  hasSmokingAllowed: boolean
  hasNotSmokingAllowed: boolean
  criteria?: FilterCriteria
  setSmokingAllowed: (smokingAllowed: SmokingAllowedCheckType) => void
  enabled: SmokingAllowedType
}) {
  const label = createLabel(criteria)
  const data: DraftSmokingAllowed = { hasNotSmokingAllowed, hasSmokingAllowed }
  const color = createColor({
    enabled: (enabled?.allow ?? false) || (enabled?.notAllow ?? false),
    isActive: criteria?.smokingAllowed === 'allow' || criteria?.smokingAllowed === 'notAllow',
  })
  return (
    <FilterChipsWithPopover
      trigger={
        <>
          {criteria?.smokingAllowed === 'notAllow' && color !== 'disabled' ? (
            <NoSmokingOutlineIcon
              width={13}
              height={13}
              className={color === 'active' ? 'text-marron-light' : 'text-neutral-600'}
            />
          ) : (
            <SmokingOutlineIcon
              width={13}
              height={13}
              className={color === 'active' ? 'text-marron-light' : 'text-neutral-600'}
            />
          )}
          {label}
        </>
      }
      data={data}
      color={color}
    >
      <Body
        criteria={criteria}
        hasSmokingAllowed={hasSmokingAllowed}
        hasNotSmokingAllowed={hasNotSmokingAllowed}
        setSmokingAllowed={setSmokingAllowed}
      />
    </FilterChipsWithPopover>
  )
}

function Body({
  criteria,
  hasSmokingAllowed,
  hasNotSmokingAllowed,
  setSmokingAllowed,
}: {
  hasSmokingAllowed: boolean
  hasNotSmokingAllowed: boolean
  criteria?: FilterCriteria
  setSmokingAllowed: (smokingAllowed: SmokingAllowedCheckType) => void
}) {
  const close = useClose()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSmokingAllowed(e.target.value as SmokingAllowedCheckType)
      close()
    },
    [close, setSmokingAllowed]
  )
  return (
    <RadioButtonPopup>
      <RadioButton
        name="smoking"
        value="allow"
        onChange={onChange}
        disabled={!hasSmokingAllowed}
        checked={criteria?.smokingAllowed === 'allow'}
      >
        喫煙
      </RadioButton>
      <RadioButton
        name="smoking"
        value="notAllow"
        onChange={onChange}
        disabled={!hasNotSmokingAllowed}
        checked={criteria?.smokingAllowed === 'notAllow'}
      >
        禁煙
      </RadioButton>
      <RadioButton
        name="smoking"
        value="unspecified"
        onChange={onChange}
        checked={
          criteria?.smokingAllowed === undefined || criteria.smokingAllowed === 'unspecified'
        }
      >
        指定なし
      </RadioButton>
    </RadioButtonPopup>
  )
}

function createLabel(criteria?: FilterCriteria) {
  if (criteria?.smokingAllowed === 'allow') {
    return '喫煙'
  }
  if (criteria?.smokingAllowed === 'notAllow') {
    return '禁煙'
  }
  return '喫煙・禁煙'
}
